namespace $.$$ {

	export type $shm_hitalama_opencode_api_session = {
		id: string
		title?: string
		slug?: string
		projectID?: string
		directory?: string
		time?: { created?: number; updated?: number }
		model?: { id?: string; modelID?: string; providerID?: string; variant?: string }
	}

	export type $shm_hitalama_opencode_api_project = {
		id: string
		worktree?: string
		vcs?: string
		icon?: { color?: string }
		time?: { created?: number; updated?: number }
	}

	export type $shm_hitalama_opencode_api_config = {
		provider?: Record< string, {
			npm?: string
			name?: string
			options?: Record< string, any >
			models?: Record< string, Record< string, any > >
		} >
		[ key: string ]: any
	}

	export type $shm_hitalama_opencode_api_provider = {
		id: string
		name?: string
		models?: Record< string, {
			id?: string
			name?: string
			providerID?: string
			limit?: {
				context?: number
				input?: number
				output?: number
			}
		} >
	}

	export type $shm_hitalama_opencode_api_agent = {
		name: string
		description?: string
		mode?: string
		hidden?: boolean
		native?: boolean
	}

	export type $shm_hitalama_opencode_api_tokens = {
		total?: number
		input?: number
		output?: number
		reasoning?: number
		cache?: {
			read?: number
			write?: number
		}
	}

	export type $shm_hitalama_opencode_api_message = {
		info: {
			sessionID?: string
			id: string
			parentID?: string
			role?: string
			type?: string
			agent?: string
			modelID?: string
			providerID?: string
			model?: { id?: string; modelID?: string; providerID?: string }
			time?: { created?: number; completed?: number }
			tokens?: $shm_hitalama_opencode_api_tokens
			cost?: number
			error?: {
				message?: string
				data?: {
					message?: string
				}
			}
		}
		parts: any[]
	}

	export type $shm_hitalama_opencode_api_question = {
		id: string
		sessionID: string
		questions: {
			question: string
			header: string
			options: {
				label: string
				description: string
			}[]
			multiple?: boolean
			custom?: boolean
		}[]
		tool?: {
			messageID: string
			callID: string
		}
	}

	export type $shm_hitalama_opencode_api_event = {
		type: string
		properties?: any
		payload?: {
			type: string
			properties?: any
		}
	}

	export class $shm_hitalama_opencode_api_error extends Error {

		constructor(
			public status: number,
			public statusText: string,
			public text: string,
		) {
			super( `${ status } ${ statusText }: ${ text }` )
		}

	}

	export class $shm_hitalama_opencode_api {
		private static credentialed_origins = new Set< string >()
		private static authenticators = new Map< string, ()=> Promise< unknown > >()
		private static authentication = new Map< string, Promise< unknown > >()
		private static header_providers = new Map< string, ()=> Record< string, string > >()

		static url( base: string, path: string ) {
			return base.replace( /\/$/, '' ) + path
		}

		static url_with_token( base: string, path: string ) {
			const token = this.token()
			const url = new URL( this.url( base, path ), $mol_dom_context.document?.location.href )
			if( token ) url.searchParams.set( 'token', token )
			return url.toString()
		}

		static credentialed( base: string, next?: boolean ) {
			const origin = new URL( base, $mol_dom_context.document?.location.href ).origin
			if( next !== undefined ) {
				if( next ) this.credentialed_origins.add( origin )
				else this.credentialed_origins.delete( origin )
			}
			return this.credentialed_origins.has( origin )
		}

		static authenticator( base: string, next?: ()=> Promise< unknown > ) {
			const origin = new URL( base, $mol_dom_context.document?.location.href ).origin
			if( next ) this.authenticators.set( origin, next )
			return this.authenticators.get( origin )
		}

		static header_provider( base: string, next?: ()=> Record< string, string > ) {
			const origin = new URL( base, $mol_dom_context.document?.location.href ).origin
			if( next ) this.header_providers.set( origin, next )
			return this.header_providers.get( origin )
		}

		static async reauthenticate( base: string ) {
			const origin = new URL( base, $mol_dom_context.document?.location.href ).origin
			const handler = this.authenticators.get( origin )
			if( !handler ) return false
			let pending = this.authentication.get( origin )
			if( !pending ) {
				pending = Promise.resolve().then( handler ).finally( ()=> this.authentication.delete( origin ) )
				this.authentication.set( origin, pending )
			}
			await pending
			return true
		}

		static token() {
			return $shm_agent_token()
		}

		static headers( base: string, init?: RequestInit ) {
			const token = this.token()
			const origin = new URL( base, $mol_dom_context.document?.location.href ).origin
			return {
				'Content-Type': 'application/json',
				...( token ? { 'X-Hitalama-Agent-Token': token } : {} ),
				... this.header_providers.get( origin )?.(),
				... init?.headers,
			}
		}

		static async json( base: string, path: string, init?: RequestInit ) {
			const request = ()=> fetch( this.url( base, path ), {
				... init,
				credentials: this.credentialed( base ) ? 'include' : 'same-origin',
				headers: this.headers( base, init ),
			} )
			let response = await request()
			if( response.status === 401 && await this.reauthenticate( base ) ) response = await request()
			if( !response.ok ) {
				const text = await response.text()
				throw new $shm_hitalama_opencode_api_error( response.status, response.statusText, text )
			}
			if( response.status === 204 ) return null
			return response.json()
		}

		static health( base: string ) {
			return this.json( base, '/global/health' )
		}

		static providers( base: string ) {
			return this.json( base, '/config/providers' )
		}

		static config( base: string ) {
			return this.json( base, '/global/config' ) as Promise< $shm_hitalama_opencode_api_config >
		}

		static config_resolved( base: string ) {
			return this.json( base, '/config' ) as Promise< $shm_hitalama_opencode_api_config >
		}

		static config_update( base: string, config: $shm_hitalama_opencode_api_config ) {
			return this.json( base, '/global/config', {
				method: 'PATCH',
				body: JSON.stringify( config ),
			} ) as Promise< $shm_hitalama_opencode_api_config >
		}

		static sessions( base: string ) {
			return this.json( base, '/session' )
		}

		static session( base: string, session_id: string, refresh = 0 ) {
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }` ) as Promise< $shm_hitalama_opencode_api_session >
		}

		static session_statuses( base: string, refresh = 0 ) {
			return this.json( base, '/session/status' ) as Promise< Record< string, { type?: string } > >
		}

		static session_update( base: string, session_id: string, patch: { title?: string; metadata?: Record< string, any > } ) {
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }`, {
				method: 'PATCH',
				body: JSON.stringify( patch ),
			} ) as Promise< $shm_hitalama_opencode_api_session >
		}

		static session_delete( base: string, session_id: string ) {
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }`, { method: 'DELETE' } )
		}

		static session_fork( base: string, session_id: string ) {
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }/fork`, {
				method: 'POST',
				body: '{}',
			} ) as Promise< $shm_hitalama_opencode_api_session >
		}

		static projects( base: string ) {
			return this.json( base, '/project' )
		}

		static agents( base: string ) {
			return this.json( base, '/agent' ) as Promise< $shm_hitalama_opencode_api_agent[] >
		}

		static async model_default( base: string ) {
			const data = await this.providers( base ) as {
				providers?: $shm_hitalama_opencode_api_provider[]
				default?: Record< string, string >
			}
			const provider = data.providers?.[0]
			if( !provider ) return null

			const model_id = data.default?.[ provider.id ] ?? Object.keys( provider.models ?? {} )[0]
			if( !model_id ) return null

			return {
				modelID: model_id,
				providerID: provider.id,
			}
		}

		static async create_session(
			base: string,
			title: string | null,
			model: { providerID: string; modelID: string } | null,
		) {
			model ??= await this.model_default( base )
			const body = {
				...( title ? { title } : {} ),
				...( model ? {
					model: {
						id: model.modelID,
						providerID: model.providerID,
					},
				} : {} ),
			}
			return this.json( base, '/session', {
				method: 'POST',
				body: JSON.stringify( body ),
			} )
		}

		static messages( base: string, session_id: string, refresh = 0 ) {
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }/message` )
		}

		static async send_prompt(
			base: string,
			session_id: string,
			text: string,
			model: { providerID: string; modelID: string } | null,
			no_reply: boolean,
			agent = '',
		) {
			model ??= await this.model_default( base )
			const body = {
				...( model ? { model } : {} ),
				...( agent ? { agent } : {} ),
				noReply: no_reply,
				parts: [
					{
						type: 'text',
						text,
					},
				],
			}
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }/message`, {
				method: 'POST',
				body: JSON.stringify( body ),
			} )
		}

		static async send_parts(
			base: string,
			session_id: string,
			parts: readonly ( { type: 'text'; text: string } | { type: 'file'; mime: string; filename?: string; url: string } )[],
			model: { providerID: string; modelID: string } | null = null,
			agent = '',
		) {
			model ??= await this.model_default( base )
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }/message`, {
				method: 'POST',
				body: JSON.stringify({
					...( model ? { model } : {} ),
					...( agent ? { agent } : {} ),
					parts,
				} ),
			} ) as Promise< $shm_hitalama_opencode_api_message >
		}

		static async send_parts_async(
			base: string,
			session_id: string,
			parts: readonly ( { type: 'text'; text: string } | { type: 'file'; mime: string; filename?: string; url: string } )[],
			model: { providerID: string; modelID: string } | null = null,
			agent = '',
		) {
			model ??= await this.model_default( base )
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }/prompt_async`, {
				method: 'POST',
				body: JSON.stringify({
					...( model ? { model } : {} ),
					...( agent ? { agent } : {} ),
					parts,
				} ),
			} )
		}

		static abort_session( base: string, session_id: string ) {
			return this.json( base, `/session/${ encodeURIComponent( session_id ) }/abort`, {
				method: 'POST',
			} )
		}

		static questions( base: string ) {
			return this.json( base, '/question' )
		}

		static question_reply( base: string, request_id: string, answers: string[][] ) {
			return this.json( base, `/question/${ encodeURIComponent( request_id ) }/reply`, {
				method: 'POST',
				body: JSON.stringify({ answers }),
			} )
		}

		static question_reject( base: string, request_id: string ) {
			return this.json( base, `/question/${ encodeURIComponent( request_id ) }/reject`, {
				method: 'POST',
			} )
		}

		static mcp_session_context(
			base: string,
			context: {
				session_id: string
				client_id?: string
				map_id?: string
				map_title?: string
				url?: string
			},
		) {
			return this.json( base, '/session/context', {
				method: 'POST',
				body: JSON.stringify( context ),
			} )
		}

	}

	export class $shm_hitalama_opencode_api_events extends $mol_object {

		private source: EventSource | null = null

		url() { return '' }
		notify() { return ( event: $shm_hitalama_opencode_api_event )=> {} }

		native() {
			if( this.source ) return this.source

			const url = $shm_hitalama_opencode_api.url_with_token( this.url(), '/global/event' )
			const source = this.source = new EventSource( url, {
				withCredentials: $shm_hitalama_opencode_api.credentialed( this.url() ),
			} )
			source.onmessage = event => $mol_wire_async( this ).message( event )
			source.onerror = event => $mol_wire_async( this ).error( event )
			for( const type of this.event_types() ) {
				source.addEventListener( type, event => $mol_wire_async( this ).message( event as MessageEvent ) )
			}

			return source
		}

		event_types() {
			return [
				'server.connected',
				'session.created',
				'session.updated',
				'session.deleted',
				'session.idle',
				'session.error',
				'session.status',
				'message.updated',
				'message.removed',
				'message.part.updated',
				'message.part.removed',
				'message.part.delta',
				'question.asked',
				'question.replied',
				'question.rejected',
			]
		}

		message( event: MessageEvent ) {
			if( !event.data ) return
			try {
				const data = JSON.parse( event.data )
				this.notify()( data.type ? data : { ... data, type: event.type } )
			} catch( error ) {
				$mol_fail_log( error )
			}
		}

		error( event: Event ) {
			// EventSource reports reconnect attempts as error events.
		}

		override destructor() {
			const source = this.source
			if( !source ) return
			source.onmessage = null
			source.onerror = null
			source.close()
			this.source = null
			super.destructor()
		}

	}

}
